<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { useVuelidate } from '@vuelidate/core';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { MarkdownToHtmlConverter } from './MarkdownToHtmlConverter';
import { HtmlToMarkdownConverter } from './HtmlToMarkdownConverter';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
    EditorContent,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      title: '',
      description: '',
      showOnSidebar: true,
      syncToChannel: false,
      color: '',
      editor: null,
      htmlToMarkdownConverter: new HtmlToMarkdownConverter(),
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    pageTitle() {
      return `${this.$t('LABEL_MGMT.EDIT.TITLE')} - ${
        this.selectedResponse.title
      }`;
    },
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  watch: {
    'selectedResponse.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.resetForm();
      }
    },
  },
  mounted() {
    this.setFormValues();
    this.initializeEditor();
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    setFormValues() {
      this.title = this.selectedResponse.title;
      this.description = this.selectedResponse.description;
      this.showOnSidebar = this.selectedResponse.show_on_sidebar;
      this.syncToChannel = this.selectedResponse.sync_to_channel;
      this.color = this.selectedResponse.color;
    },
    resetForm() {
      this.setFormValues();
      this.v$?.$reset?.();
      if (this.editor) {
        const initialHtml = this.description
          ? MarkdownToHtmlConverter.convertToHtml(this.description)
          : '<p></p>';
        this.editor.commands.setContent(initialHtml, false);
      }
    },
    onCancel() {
      this.resetForm();
      this.onClose();
    },
    initializeEditor() {
      // Convert markdown to HTML for initial content
      const initialHtml = this.description
        ? MarkdownToHtmlConverter.convertToHtml(this.description)
        : '<p></p>';

      this.editor = new Editor({
        content: initialHtml,
        extensions: [StarterKit],
        onUpdate: ({ editor }) => {
          // Convert HTML content to markdown
          const htmlContent = editor.getHTML();
          this.description =
            this.htmlToMarkdownConverter.convertToMarkdown(htmlContent);
          // Touch validation to trigger validation check
          this.v$.description.$touch();
        },
        editorProps: {
          attributes: {
            class:
              'tiptap-editor prose max-w-none min-h-[120px] p-3 border rounded-md border-slate-200 dark:border-slate-600 focus-within:border-woot-500 dark:focus-within:border-woot-500',
            'data-placeholder': this.$t(
              'LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER'
            ),
          },
        },
      });
    },
    editLabel() {
      // Get the final markdown content by converting from HTML
      const htmlContent = this.editor.getHTML();
      const markdownContent =
        this.htmlToMarkdownConverter.convertToMarkdown(htmlContent);

      this.$store
        .dispatch('labels/update', {
          id: this.selectedResponse.id,
          color: this.color,
          description: markdownContent,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
          sync_to_channel: this.syncToChannel,
        })
        .then(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => this.onClose(), 10);
        })
        .catch(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.ERROR_MESSAGE'));
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-wrap mx-0" @submit.prevent="editLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <div class="w-full">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
        >
          {{ $t('LABEL_MGMT.FORM.DESCRIPTION.LABEL') }}
        </label>
        <div :class="{ error: v$.description.$error }">
          <EditorContent
            v-if="editor"
            :editor="editor"
            class="description-editor"
          />
        </div>
        <p v-if="v$.description.$error" class="text-sm text-red-500 mt-1">
          {{ $t('LABEL_MGMT.FORM.DESCRIPTION.ERROR') }}
        </p>
      </div>

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="show_on_sidebar">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="syncToChannel" type="checkbox" :value="true" />
        <label for="sync_to_channel">
          {{ $t('LABEL_MGMT.FORM.SYNC_TO_CHANNEL.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onCancel"
        />
        <NextButton
          type="submit"
          :label="$t('LABEL_MGMT.FORM.EDIT')"
          :disabled="v$.title.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      @apply lowercase;
    }
  }
}

// TipTap editor styles
::v-deep .description-editor {
  .tiptap-editor {
    outline: none;
    background-color: white;

    &:focus {
      outline: none;
      border-color: var(--woot-color-woot-500);
    }
  }

  .tiptap-editor p {
    margin: 0.5rem 0;
  }

  .tiptap-editor p:first-child {
    margin-top: 0;
  }

  .tiptap-editor p:last-child {
    margin-bottom: 0;
  }

  .tiptap-editor ul,
  .tiptap-editor ol {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }

  .tiptap-editor h1,
  .tiptap-editor h2,
  .tiptap-editor h3,
  .tiptap-editor h4,
  .tiptap-editor h5,
  .tiptap-editor h6 {
    margin: 1rem 0 0.5rem 0;
    font-weight: bold;
  }

  .tiptap-editor h1 {
    font-size: 1.5rem;
  }

  .tiptap-editor h2 {
    font-size: 1.3rem;
  }

  .tiptap-editor h3 {
    font-size: 1.1rem;
  }

  .tiptap-editor strong {
    font-weight: bold;
  }

  .tiptap-editor em {
    font-style: italic;
  }

  .tiptap-editor code {
    background-color: #f3f4f6;
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-family: monospace;
    font-size: 0.9em;
  }

  .tiptap-editor blockquote {
    border-left: 3px solid #d1d5db;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
  }

  .tiptap-editor.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
  }
}

// Dark mode styles
:root.dark {
  ::v-deep .description-editor {
    .tiptap-editor {
      background-color: rgb(51, 65, 85);
      color: rgb(226, 232, 240);

      code {
        background-color: #374151;
        color: #e5e7eb;
      }

      blockquote {
        border-left-color: #6b7280;
      }
    }
  }
}
</style>
